import {
  encodeNicePayReserved,
  saveNicePayPending,
  type NicePayPending,
} from "@/lib/payments/nicepay-pending";

export type PortOnePayMethod = "card" | "trans" | "vbank" | "phone" | "kakaopay";

/** 실제 PG: 나이스페이 (직접 연동) */
export const PORTONE_PG_PROVIDER = "nice";

export interface PortOnePaymentRequest {
  merchantUid: string;
  name: string;
  amount: number;
  payMethod?: PortOnePayMethod;
  buyerName?: string;
  buyerEmail?: string;
  buyerTel?: string;
  redirectUrl?: string;
  pending?: Omit<NicePayPending, "orderId" | "amount">;
}

export interface PortOnePaymentResult {
  success: boolean;
  redirecting?: boolean;
  paymentId?: string;
  /** NicePay tid. 기존 필드명 호환용 */
  impUid?: string;
  merchantUid?: string;
  paidAmount?: number;
  payMethod?: string;
  pgProvider?: string;
  errorMessage?: string;
}

function resolveClientId(): string | null {
  return process.env.NEXT_PUBLIC_NICEPAY_CLIENT_ID?.trim() || null;
}

export function getPortOnePaymentConfigLabel(): string {
  const clientId = resolveClientId();
  if (!clientId) return "미설정";
  return `나이스페이 / ${clientId.slice(0, 10)}…`;
}

export function isPortOnePaymentConfigured(): boolean {
  return Boolean(resolveClientId());
}

const MAX_ORDER_ID_LENGTH = 64;

export function toPortOnePaymentId(value: string): string {
  const sanitized = value.replace(/[^a-zA-Z0-9]/g, "");
  if (sanitized.length > 0) {
    return sanitized.slice(0, MAX_ORDER_ID_LENGTH);
  }
  return `pay${Date.now()}`.slice(0, MAX_ORDER_ID_LENGTH);
}

/** 나이스페이 상품명: 40바이트, " 와 ¦ 불가 */
export function toNiceOrderName(name: string): string {
  const cleaned = name
    .replace(/["¦|]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const encoder = new TextEncoder();
  if (encoder.encode(cleaned).length <= 40) return cleaned || "ConnecTone";

  let end = cleaned.length;
  while (end > 0 && encoder.encode(cleaned.slice(0, end)).length > 40) {
    end -= 1;
  }
  return cleaned.slice(0, end).trim() || "ConnecTone";
}

function mapPayMethod(method?: PortOnePayMethod): string {
  switch (method) {
    case "trans":
      return "bank";
    case "vbank":
      return "vbank";
    case "phone":
      return "cellphone";
    case "kakaopay":
      return "kakaopay";
    default:
      return "card";
  }
}

function digitsOnly(value?: string): string | undefined {
  if (!value) return undefined;
  const digits = value.replace(/\D/g, "");
  return digits || undefined;
}

function loadNicePayScript(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("브라우저에서만 결제를 시작할 수 있습니다."));
  }
  if (window.AUTHNICE) return Promise.resolve();

  const existing = document.querySelector<HTMLScriptElement>(
    'script[src="https://pay.nicepay.co.kr/v1/js/"]'
  );
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("나이스페이 결제 스크립트를 불러오지 못했습니다."))
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://pay.nicepay.co.kr/v1/js/";
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("나이스페이 결제 스크립트를 불러오지 못했습니다."));
    document.head.appendChild(script);
  });
}

/**
 * 나이스페이 결제창(Server 승인)을 엽니다.
 * 인증 후 `/api/payments/nicepay/return`으로 이동하므로 페이지가 떠납니다.
 */
export async function requestPortOnePayment(
  request: PortOnePaymentRequest
): Promise<PortOnePaymentResult> {
  const clientId = resolveClientId();
  if (!clientId) {
    return {
      success: false,
      errorMessage:
        "나이스페이 설정이 필요합니다. NEXT_PUBLIC_NICEPAY_CLIENT_ID를 Vercel에 추가하세요.",
    };
  }

  const orderId = toPortOnePaymentId(request.merchantUid);
  const pending: NicePayPending = {
    kind: request.pending?.kind || "test",
    orderId,
    amount: request.amount,
    requestId: request.pending?.requestId,
    bookingId: request.pending?.bookingId,
    reservedBookingId: request.pending?.reservedBookingId,
    isProAssigned: request.pending?.isProAssigned,
    productName: request.pending?.productName,
    optionLabel: request.pending?.optionLabel,
    fileName: request.pending?.fileName,
  };
  saveNicePayPending(pending);

  const returnUrl =
    request.redirectUrl ||
    (typeof window !== "undefined"
      ? `${window.location.origin}/api/payments/nicepay/return`
      : undefined);

  try {
    await loadNicePayScript();
    if (!window.AUTHNICE) {
      return {
        success: false,
        errorMessage: "나이스페이 결제창을 초기화하지 못했습니다.",
      };
    }

    window.AUTHNICE.requestPay({
      clientId,
      method: mapPayMethod(request.payMethod),
      orderId,
      amount: request.amount,
      goodsName: toNiceOrderName(request.name),
      returnUrl,
      mallReserved: encodeNicePayReserved(pending),
      buyerName: request.buyerName,
      buyerEmail: request.buyerEmail,
      buyerTel: digitsOnly(request.buyerTel),
      language: "KO",
      zIdxHigher: true,
      ...(request.payMethod === "phone" ? { isDigital: true } : {}),
      fnError: (result: { errorMsg?: string }) => {
        const message = result?.errorMsg || "결제창에서 오류가 발생했습니다.";
        if (typeof window !== "undefined") {
          window.alert(message);
        }
      },
    });

    return {
      success: false,
      redirecting: true,
      paymentId: orderId,
      merchantUid: orderId,
      paidAmount: request.amount,
      payMethod: request.payMethod || "card",
      pgProvider: PORTONE_PG_PROVIDER,
    };
  } catch (error) {
    return {
      success: false,
      errorMessage:
        error instanceof Error ? error.message : "결제 중 오류가 발생했습니다.",
    };
  }
}
